import { isAbortError, streamSse, type ApiClient } from '@nyce/admin-kit/api';
import { dbApi, isActive, type Job, type LogLine } from '../../api/db';

export type StreamFn = typeof streamSse;

export interface FollowJobOptions {
  client: ApiClient;
  jobId: number;
  /** Last log seq already shown (0 = from the start). */
  after?: number;
  onLog: (lines: LogLine[]) => void;
  onJob: (job: Job) => void;
  /** `stream` while SSE works, `poll` after falling back, `done` once the job has ended. */
  onMode?: (mode: 'stream' | 'poll' | 'done') => void;
  signal: AbortSignal;
  /** Injected in tests. */
  stream?: StreamFn;
  /** Wait before a reconnect / between polls. */
  retryMs?: number;
  pollMs?: number;
  /** Consecutive stream failures (errors or drops without a single event) before falling back to polling. */
  maxFailures?: number;
}

const sleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    if (signal.aborted || ms <= 0) return resolve();
    const t = setTimeout(resolve, ms);
    signal.addEventListener('abort', () => (clearTimeout(t), resolve()), { once: true });
  });

/**
 * Follow a job's log and status until it ends: SSE `GET /jobs/{id}/stream?after=<last seq>` (events `log`, `job`, `end`),
 * reconnecting with the last seq seen when the stream drops; after `maxFailures` fruitless attempts it falls back to
 * polling `GET /jobs/{id}/log?after=` and `GET /jobs/{id}`. Lines are de-duplicated by seq. Resolves when the job has
 * ended or `signal` aborts.
 */
export async function followJob(o: FollowJobOptions): Promise<void> {
  const api = dbApi(o.client);
  const stream = o.stream ?? streamSse;
  const retryMs = o.retryMs ?? 1500;
  const pollMs = o.pollMs ?? 2000;
  const maxFailures = o.maxFailures ?? 3;
  let lastSeq = o.after ?? 0;
  let failures = 0;
  let ended = false;

  const addLines = (lines: LogLine[]) => {
    const fresh = lines.filter((l) => l.seq > lastSeq).sort((a, b) => a.seq - b.seq);
    if (!fresh.length) return;
    lastSeq = fresh[fresh.length - 1].seq;
    o.onLog(fresh);
  };

  /** The log endpoint returns at most 2000 lines per call: read until a short page. */
  const drainLog = async () => {
    for (;;) {
      const lines = await api.jobs.log(o.jobId, lastSeq);
      addLines(lines);
      if (lines.length < 2000 || o.signal.aborted) return;
    }
  };

  o.onMode?.('stream');
  while (!ended && !o.signal.aborted && failures < maxFailures) {
    let events = 0;
    try {
      await stream(
        o.client,
        'GET',
        api.jobs.streamPath(o.jobId, lastSeq),
        undefined,
        (event, data) => {
          events++;
          if (event === 'log') addLines([data as LogLine]);
          else if (event === 'job') o.onJob(data as Job);
          else if (event === 'end') ended = true;
        },
        o.signal
      );
    } catch (e) {
      if (isAbortError(e) || o.signal.aborted) return;
    }
    if (ended || o.signal.aborted) break;
    failures = events ? 0 : failures + 1;
    await sleep(events ? 0 : retryMs, o.signal);
  }
  if (o.signal.aborted) return;
  if (ended) {
    o.onMode?.('done');
    return;
  }

  // Streaming does not work here (proxy buffering, old browser …): poll instead.
  o.onMode?.('poll');
  while (!o.signal.aborted) {
    try {
      await drainLog();
      const job = await api.jobs.get(o.jobId);
      if (o.signal.aborted) return;
      o.onJob(job);
      if (!isActive(job.status)) {
        // one more read: lines written between the log call and the status call
        await drainLog();
        o.onMode?.('done');
        return;
      }
    } catch (e) {
      if (isAbortError(e) || o.signal.aborted) return;
    }
    await sleep(pollMs, o.signal);
  }
}
